import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, Chip, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Status, Wrapper } from "@googlemaps/react-wrapper";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

export const CoordinatesMapEditor = React.memo(({ coordinates = [], onChange, maxPoints = 4 }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polygonRef = useRef(null);
  const markersRef = useRef([]);
  const pendingMarkerRef = useRef(null);
  const coordinatesRef = useRef(coordinates);
  const geocoderRef = useRef(null);
  const [pendingPoint, setPendingPoint] = useState(null);
  const [searchAddress, setSearchAddress] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    coordinatesRef.current = coordinates;
  }, [coordinates]);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const initialCoords = coordinatesRef.current;
    const defaultCenter = initialCoords.length > 0
      ? { lat: initialCoords[0].latitude, lng: initialCoords[0].longitude }
      : { lat: -15.792487, lng: -47.893526 };

    const mapInstance = new google.maps.Map(mapRef.current, {
      center: defaultCenter,
      zoom: initialCoords.length > 0 ? 18 : 4,
      mapId: import.meta.env.VITE_MAP_ID || 'd06eb9fab6b219cd',
    });
    mapInstanceRef.current = mapInstance;
    geocoderRef.current = new google.maps.Geocoder();

    mapInstance.addListener('click', (e) => {
      if (coordinatesRef.current.length >= maxPoints) return;
      setPendingPoint({ latitude: e.latLng.lat(), longitude: e.latLng.lng() });
    });

    if (initialCoords.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      initialCoords.forEach(coord => bounds.extend({ lat: coord.latitude, lng: coord.longitude }));
      mapInstance.fitBounds(bounds, 50);
    }
  }, [maxPoints]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    if (pendingMarkerRef.current) {
      pendingMarkerRef.current.map = null;
      pendingMarkerRef.current = null;
    }

    if (pendingPoint) {
      const markerContent = document.createElement('div');
      markerContent.style.cssText = `width:30px;height:30px;border-radius:50%;background-color:#ff9800;border:3px dashed white;box-shadow:0 2px 6px rgba(0,0,0,0.4);animation:pulse 1s infinite;`;
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: pendingPoint.latitude, lng: pendingPoint.longitude },
        map,
        content: markerContent,
      });
      pendingMarkerRef.current = marker;
    }
  }, [pendingPoint]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    markersRef.current.forEach(m => { m.map = null; });
    markersRef.current = [];

    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    if (coordinates.length === 0) return;

    const paths = coordinates.map(coord => ({ lat: coord.latitude, lng: coord.longitude }));

    paths.forEach((path, index) => {
      const markerContent = document.createElement('div');
      markerContent.style.cssText = `width:28px;height:28px;border-radius:50%;background-color:#1976d2;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;cursor:pointer;`;
      markerContent.textContent = String(index + 1);

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: path,
        map,
        content: markerContent,
        title: `Ponto ${index + 1} - Clique para remover`,
      });
      marker.addListener('click', () => onChange(coordinates.filter((_, i) => i !== index)));
      markersRef.current.push(marker);
    });

    if (coordinates.length >= 3) {
      const polygon = new google.maps.Polygon({
        paths,
        strokeColor: '#666666',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: '#999999',
        fillOpacity: 0.35,
      });
      polygon.setMap(map);
      polygonRef.current = polygon;
    }
  }, [coordinates, onChange]);

  const handleConfirm = useCallback(() => {
    if (!pendingPoint) return;
    onChange([...coordinates, pendingPoint]);
    setPendingPoint(null);
  }, [pendingPoint, coordinates, onChange]);

  const handleCancel = useCallback(() => setPendingPoint(null), []);
  const handleClearAll = useCallback(() => { onChange([]); setPendingPoint(null); }, [onChange]);

  const handleSearch = useCallback(() => {
    if (!searchAddress.trim() || !geocoderRef.current || !mapInstanceRef.current) return;
    setSearching(true);
    geocoderRef.current.geocode({ address: searchAddress }, (results, status) => {
      setSearching(false);
      if (status === 'OK' && results[0]) {
        mapInstanceRef.current.setCenter(results[0].geometry.location);
        mapInstanceRef.current.setZoom(18);
      }
    });
  }, [searchAddress]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSearch(); }
  }, [handleSearch]);

  return (
    <Box sx={{ width: '100%', maxWidth: 800 }}>
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar endereço (ex: Rua das Flores, 123, São Paulo)"
          value={searchAddress}
          onChange={(e) => setSearchAddress(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          disabled={searching}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch} disabled={searching || !searchAddress.trim()} size="small">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="textSecondary">
          Clique no mapa para adicionar pontos ({coordinates.length}/{maxPoints})
        </Typography>
        {coordinates.length > 0 && (
          <IconButton size="small" onClick={handleClearAll} title="Limpar todos">
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <div ref={mapRef} style={{ height: 400, minHeight: 400, width: '100%', minWidth: 300, borderRadius: 8, border: '1px solid #ddd' }} />

      {pendingPoint && (
        <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`Lat: ${pendingPoint.latitude.toFixed(6)}, Lng: ${pendingPoint.longitude.toFixed(6)}`} size="small" color="warning" />
          <IconButton size="small" color="success" onClick={handleConfirm} title="Confirmar ponto"><CheckIcon /></IconButton>
          <IconButton size="small" color="error" onClick={handleCancel} title="Cancelar"><UndoIcon /></IconButton>
        </Box>
      )}

      {coordinates.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {coordinates.map((coord, index) => (
            <Chip
              key={index}
              label={`${index + 1}: ${coord.latitude.toFixed(5)}, ${coord.longitude.toFixed(5)}`}
              size="small"
              onDelete={() => onChange(coordinates.filter((_, i) => i !== index))}
            />
          ))}
        </Box>
      )}
    </Box>
  );
});

const CoordinatesMapInput = () => {
  const form = useFormContext();
  const coordinates = form.watch('coordinates') || [];

  const handleChange = useCallback((newCoords) => {
    form.setValue('coordinates', newCoords, { shouldDirty: true });
  }, [form]);

  return (
    <Wrapper
      apiKey={import.meta.env.VITE_MAP_API_KEY || 'AIzaSyA2-gtjUXGbaFwbFy8pmjTPgoV4pTLbx2g'}
      libraries={['marker']}
      render={(status) => {
        if (status === Status.LOADING) return <Typography>Carregando mapa...</Typography>;
        if (status === Status.FAILURE) return <Typography color="error">Erro ao carregar mapa</Typography>;
        return null;
      }}
    >
      <CoordinatesMapEditor coordinates={coordinates} onChange={handleChange} maxPoints={4} />
    </Wrapper>
  );
};

export default CoordinatesMapInput;
